import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { translate } from "base/shared/Localization";

import {
  Heading,
  Text,
  Flex,
  Spacer,
  Button,
  TextArea,
  Checkbox,
} from "@badi/badi-components";

import UserProfileBadge from "components/user-profile-badge";
import RoomDetailBadge from "components/room-detail-badge";
import BlockSeparator from "components/block-separator";
import { getSavedMessageEnquiry } from "datamodel/Conversation/api";

import { CharCounter, Form, TextAreaWrapper } from "./styled-components";

import { MESSAGE_ENQUIRY_MAX_LENGTH } from "./constants";

const MessageEnquiry = ({
  roomPicture,
  roomTitle,
  roomAddress,
  roomId,
  disabled,
  owner = null,
  description = "",
  onSubmit,
  title = "",
  placeholder = "",
  buttonText = "",
  messageRequired = true,
  token,
  isSaveMessageAvailable,
}) => {
  const [canSendMessage, setMessageValidation] = useState(!messageRequired);
  const [textMessage, setTextMessage] = useState("");
  const [isSaveMessageEnabled, setIsSaveMessageEnabled] = useState(
    isSaveMessageAvailable,
  );

  useEffect(() => {
    if (isSaveMessageAvailable) {
      getSavedMessageEnquiry(token).then(({ data }) => {
        const message = data.default?.message;
        if (message) {
          setTextMessage(message);
          setMessageValidation(true);
        }
      });
    }
  }, []);

  const handleToggleCheckbox = () => {
    setIsSaveMessageEnabled(!isSaveMessageEnabled);
  };

  const handleChange = useCallback(
    (event) => {
      const { value } = event.target;

      const isMaxReached = value.length > MESSAGE_ENQUIRY_MAX_LENGTH;
      if (!isMaxReached) {
        if (messageRequired) {
          setMessageValidation(!!value.trim());
        }
        setTextMessage(value);
      }
    },
    [messageRequired],
  );

  const handleOnSubmit = useCallback((event) => {
    event.preventDefault();

    onSubmit({
      message: textMessage,
      saveMessage: isSaveMessageEnabled,
    });
  });

  return (
    <Form onSubmit={handleOnSubmit}>
      <Flex direction="column" fullHeight={true}>
        <Spacer bottom={1}>
          <Heading data-qa="message-enquiry-heading">{title}</Heading>
        </Spacer>
        {description && (
          <Text body={3} data-qa="message-enquiry-description">
            {description}
          </Text>
        )}
        <BlockSeparator>
          {owner && (
            <UserProfileBadge
              {...owner}
              avatar={owner.picture}
              clickable={false}
              data-qa="user-profile-badge"
            />
          )}
        </BlockSeparator>
        <BlockSeparator>
          <TextAreaWrapper>
            <TextArea
              canSendMessage={canSendMessage}
              name="enquiry-message"
              onChange={handleChange}
              placeholder={placeholder}
              value={textMessage}
            />
          </TextAreaWrapper>
          <Flex
            alignItems="center"
            justify={isSaveMessageAvailable ? "spaceBetween" : "flexEnd"}
          >
            {isSaveMessageAvailable && (
              <Checkbox
                checked={isSaveMessageEnabled}
                data-qa="enquiry-message-checkbox"
                onChange={handleToggleCheckbox}
              >
                <Text body="12-Regular" dataQa="enquiry-message-checkbox-label">
                  {translate("enquiry.message.checkbox")}
                </Text>
              </Checkbox>
            )}
            <CharCounter
              canSendMessage={canSendMessage}
              data-qa="enquiry-message-counter"
            >
              {translate("characters_left", {
                smart_count: MESSAGE_ENQUIRY_MAX_LENGTH - textMessage.length,
              })}
            </CharCounter>
          </Flex>
        </BlockSeparator>
        <BlockSeparator grow={1}>
          <RoomDetailBadge
            address={roomAddress}
            clickable={false}
            data-qa="room-detail-badge"
            id={roomId}
            picture={roomPicture}
            title={roomTitle}
          />
        </BlockSeparator>
        <Button
          dataQa="message-enquiry-submit"
          disabled={disabled || !canSendMessage}
          fullWidth={true}
          type="submit"
        >
          {buttonText}
        </Button>
      </Flex>
    </Form>
  );
};

MessageEnquiry.propTypes = {
  buttonText: PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  isSaveMessageAvailable: PropTypes.bool.isRequired,
  messageRequired: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  owner: PropTypes.shape({
    languages: PropTypes.array.isRequired,
    name: PropTypes.string.isRequired,
    picture: PropTypes.string.isRequired,
  }),
  placeholder: PropTypes.string,
  roomAddress: PropTypes.string.isRequired,
  roomId: PropTypes.number.isRequired,
  roomPicture: PropTypes.string.isRequired,
  roomTitle: PropTypes.string.isRequired,
  title: PropTypes.string,
  token: PropTypes.string.isRequired,
};

export default MessageEnquiry;
